import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Platform, isPlatform } from '@ionic/angular';
import { StatusBar, Style } from '@capacitor/status-bar';
import { filter, Subscription } from 'rxjs';

export type StatusBarConfig = {
  mainColor: string; // base status bar color
  brandColor: string; // brand color (e.g., for welcome)
  showOn?: string[]; // show ONLY on these routes (takes precedence over hideOn)
  hideOn?: string[]; // hide on these routes
  brandOn?: string[]; // where to use brandColor instead of mainColor
  overlayOn?: string[]; // where to place content UNDER the status bar (iOS)
};

@Injectable({ providedIn: 'root' })
export class StatusBarService implements OnDestroy {
  private router = inject(Router);
  private platform = inject(Platform);

  private isInitialized = false;
  private sub?: Subscription;
  private cfg: StatusBarConfig = {
    mainColor: '#FFFFFF',
    brandColor: '#FF6700',
    hideOn: [],
    brandOn: [],
    overlayOn: []
  };

  /** Initialization + subscribe to routing */
  async init(config?: Partial<StatusBarConfig>) {
    if (this.isInitialized) return;

    if (config) this.cfg = { ...this.cfg, ...config };
    await this.platform.ready();

    // IMPORTANT: remove any old calls like StatusBar.setStyle({ style: Style.Light }) elsewhere.
    // From now on, only this service sets style/color.

    await this.applyForUrl(this.router.url);

    this.sub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.applyForUrl(e.urlAfterRedirects || e.url));

    this.isInitialized = true;
  }

  /** Update config on the fly */
  async updateConfig(patch: Partial<StatusBarConfig>) {
    this.cfg = { ...this.cfg, ...patch };
    await this.applyForUrl(this.router.url);
  }

  /** Manually re-apply (e.g., after theme change) */
  async refresh() {
    await this.applyForUrl(this.router.url);
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  // ================= CORE =================

  private async applyForUrl(url: string) {
    if (!this.platform.is('capacitor')) return;

    const shouldShow = this.shouldShow(url);

    if (!shouldShow) {
      try {
        await StatusBar.hide();
      } catch (e) {
        console.error(e);
      }
      return;
    }

    try {
      await StatusBar.show();
    } catch (e) {
      console.error(e);
    }

    // 1) overlay: on iOS the status bar background color is visible only when overlay=false
    const overlay = isPlatform('ios') && this.matches(url, this.cfg.overlayOn);
    try {
      await StatusBar.setOverlaysWebView({ overlay });
    } catch (e) {
      console.error(e);
    }

    // 2) color: brand or main
    const color = this.matches(url, this.cfg.brandOn)
      ? this.cfg.brandColor
      : this.cfg.mainColor;

    // 3) set background first (Android actually paints the bar; on iOS it matters only when overlay=false)
    try {
      await StatusBar.setBackgroundColor({ color });
    } catch (e) {
      console.error(e);
    }

    // 4) then set icon/text style
    const lightBg = this.isLight(color);
    const desiredStyle = lightBg ? Style.Light : Style.Dark;

    try {
      await StatusBar.setStyle({ style: desiredStyle });
    } catch (e) {
      console.error(e);
    }

    // 5) safeguard against late overrides (e.g., somewhere init set Style.Light)
    //    wait one frame and set the style again
    if (typeof window !== 'undefined') {
      requestAnimationFrame(async () => {
        try {
          await StatusBar.setStyle({ style: desiredStyle });
        } catch (e) {
          console.error(e);
        }
      });
    }
  }

  private shouldShow(url: string): boolean {
    const { showOn, hideOn } = this.cfg;
    if (showOn?.length) return this.matches(url, showOn);
    if (hideOn?.length) return !this.matches(url, hideOn);
    return true;
  }

  /** Supports ‘/auth/*’ prefixes and exact paths */
  private matches(url: string, list?: string[]): boolean {
    if (!list || !list.length) return false;
    return list.some((p) =>
      p.endsWith('/*') ? url.startsWith(p.slice(0, -1)) : url === p
    );
  }

  private isLight(hex: string): boolean {
    const h = hex.replace('#', '');
    const r = parseInt(h.length === 3 ? h[0] + h[0] : h.slice(0, 2), 16);
    const g = parseInt(h.length === 3 ? h[1] + h[1] : h.slice(2, 4), 16);
    const b = parseInt(h.length === 3 ? h[2] + h[2] : h.slice(4, 6), 16);
    // perceived luminance
    const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return L > 180;
  }
}
